import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Ruler, Plus, Trash2, LayoutGrid, LogOut, ArrowRight, Film, Home, Sofa } from "lucide-react";
import { totalArea } from "../lib/blueprint";
import { toast } from "sonner";
import RedesignStudio from "../components/RedesignStudio";

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [redesign, setRedesign] = useState(null); // "exterior" | "interior" | null

  const load = async () => {
    try { const res = await api.get("/projects"); setProjects(res.data); }
    catch { toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/projects/${id}`);
    setProjects((p) => p.filter((x) => x.id !== id));
    toast.success("Project deleted");
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]">
      <header className="sticky top-0 z-40 glass border-b border-[#30363D]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Ruler className="w-6 h-6 text-[#38BDF8]" />
            <span className="font-heading text-xl font-bold">ArchiMesh<span className="text-[#38BDF8]">AI</span></span>
          </Link>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-[#30363D]" />}
            <span className="text-sm text-[#8B949E] hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn" className="text-[#8B949E] hover:text-[#EF4444]"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold">Your Projects</h1>
            <p className="text-[#8B949E] mt-1 text-sm">Blueprints saved to your account</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setRedesign("exterior")} variant="outline" className="border-[#30363D] text-[#38BDF8] hover:bg-[#38BDF8]/10 font-semibold" data-testid="redesign-exterior-btn">
              <Home className="w-4 h-4 mr-1" /> Redesign Exterior
            </Button>
            <Button onClick={() => setRedesign("interior")} variant="outline" className="border-[#30363D] text-[#F59E0B] hover:bg-[#F59E0B]/10 font-semibold" data-testid="design-interior-btn">
              <Sofa className="w-4 h-4 mr-1" /> Design Interior
            </Button>
            <Button onClick={() => navigate("/studio", { state: { fresh: true } })} className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="new-project-btn">
              <Plus className="w-4 h-4 mr-1" /> New Blueprint
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-[#8B949E] text-sm">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#30363D] bg-[#161B22] p-16 text-center">
            <LayoutGrid className="w-10 h-10 mx-auto text-[#484F58]" />
            <p className="mt-4 text-[#8B949E]">No projects yet. Start your first blueprint.</p>
            <Button onClick={() => navigate("/studio", { state: { fresh: true } })} className="mt-5 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="empty-new-project-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Blueprint
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <div key={p.id} onClick={() => navigate("/studio", { state: { projectId: p.id } })}
                className="group rounded-2xl border border-[#30363D] bg-[#161B22] p-5 cursor-pointer hover:border-[#38BDF8] transition-colors" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-heading font-semibold text-lg group-hover:text-[#38BDF8] transition-colors">{p.name}</h3>
                  <button onClick={(e) => del(p.id, e)} className="text-[#6E7681] hover:text-[#EF4444]" data-testid={`delete-project-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-[#8B949E] mt-1 font-mono-cad">{p.city}</p>
                <div className="flex gap-3 mt-4 text-xs text-[#8B949E] font-mono-cad items-center">
                  <span>{(p.blueprint?.rooms || []).length} rooms</span>
                  <span>·</span>
                  <span>{totalArea(p.blueprint?.rooms)} ft²</span>
                  {p.video_path && (
                    <span className="ml-auto flex items-center gap-1 text-[#A855F7]" data-testid={`project-video-badge-${p.id}`}>
                      <Film className="w-3.5 h-3.5" /> walkabout
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-center text-xs text-[#38BDF8] opacity-0 group-hover:opacity-100 transition-opacity">Open in studio <ArrowRight className="w-3 h-3 ml-1" /></div>
              </div>
            ))}
          </div>
        )}
      </main>
      <RedesignStudio open={!!redesign} mode={redesign || "exterior"} onOpenChange={(v) => { if (!v) setRedesign(null); }} />
    </div>
  );
}
